/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState } from 'react';
import { Tabs, Tab, styled, Box, TabsProps, TabProps, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'customBackgroundColor',
})<{ customBackgroundColor?: string }>(({ theme, customBackgroundColor }) => ({
  minHeight: 40,
  backgroundColor: customBackgroundColor || theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius * 3, // 24px with default borderRadius of 8
  padding: theme.spacing(0.5),
  position: 'relative',
  '& .MuiTabs-indicator': {
    display: 'none', // Hide the default indicator
  },
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(0.5),
    position: 'relative',
  },
}));

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => !['customActiveTextColor', 'customInactiveTextColor'].includes(prop as string),
})<{
  customActiveTextColor?: string;
  customInactiveTextColor?: string;
}>(({ theme, customActiveTextColor, customInactiveTextColor }) => ({
  minHeight: 32,
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: 'inherit',
  marginRight: 0,
  marginLeft: 0,
  borderRadius: theme.shape.borderRadius * 2.5, // 20px
  color: customInactiveTextColor || theme.palette.text.secondary,
  zIndex: 2,
  position: 'relative',
  padding: '6px 32px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  transition: theme.transitions.create([
    'color',
  ], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    color: customActiveTextColor || theme.palette.primary.main,
  },
  '&.Mui-selected': {
    color: customActiveTextColor || theme.palette.primary.contrastText,
  },
  '&.Mui-focusVisible': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
}));

// Animated sliding indicator with dynamic background
const SlidingIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'customActiveBackgroundColor',
})<{ customActiveBackgroundColor?: string }>(({ theme, customActiveBackgroundColor }) => ({
  position: 'absolute',
  paddingBlock: theme.spacing(2),
  backgroundColor: customActiveBackgroundColor || theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius * 2.5, // 20px
  transition: theme.transitions.create([
    'left',
    'width',
    'transform',
  ], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  zIndex: 1,
}));

// Interface for custom tab props
export interface CustomTabProps extends Omit<TabProps, 'children'> {
  label?: string;
  labelKey?: string; // Translation key
  value: string | number;
  icon?: React.ReactElement;
}

// Interface for custom tabs props
export interface ChipTabsProps extends Omit<TabsProps, 'children'> {
  value: string | number;
  onChange: (event: React.SyntheticEvent, newValue: string | number) => void;
  tabs: CustomTabProps[];
  variant?: 'standard' | 'fullWidth';
  size?: 'small' | 'medium' | 'large';
  backgroundColor?: string;
  activeBackgroundColor?: string;
  activeTextColor?: string;
  inactiveTextColor?: string;
  namespace?: string; // Translation namespace
}

const ChipTabs: React.FC<ChipTabsProps> = ({
  value,
  onChange,
  tabs,
  variant = 'standard',
  size = 'medium',
  backgroundColor: defaultBackgroundColor,
  activeBackgroundColor: defaultActiveBackgroundColor,
  activeTextColor: defaultActiveTextColor,
  inactiveTextColor: defaultInactiveTextColor,
  namespace = 'common',
  ...props
}) => {
  const theme = useTheme();
  const tabsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  const backgroundColor = defaultBackgroundColor || 'transparent';
  const activeBackgroundColor = defaultActiveBackgroundColor || theme.palette.pink[400];
  const activeTextColor = defaultActiveTextColor || theme.palette.common.white;
  const inactiveTextColor = defaultInactiveTextColor || theme.palette.common.white;

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          minHeight: 32,
          '& .MuiTab-root': {
            minHeight: 24,
            fontSize: 'inherit',
            padding: '4px 12px',
          },
        };
      case 'large':
        return {
          minHeight: 56,
          '& .MuiTab-root': {
            minHeight: 48,
            fontSize: 'inherit',
            padding: '12px 24px',
          },
        };
      default:
        return {};
    }
  };

  const updateIndicatorPosition = () => {
    if (!tabsRef.current) return;

    const activeTabIndex = tabs.findIndex(tab => tab.value === value);
    if (activeTabIndex === -1) return;

    const tabElements = tabsRef.current.querySelectorAll('.MuiTab-root');
    const activeTab = tabElements[activeTabIndex] as HTMLElement;

    if (activeTab) {
      const containerRect = tabsRef.current.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      // Account for the container padding (4px on each side)
      const containerPadding = 4;

      setIndicatorStyle({
        left: tabRect.left - containerRect.left - containerPadding,
        width: tabRect.width,
      });
    }
  };

  useEffect(() => {
    // Update indicator position when value changes
    updateIndicatorPosition();
  }, [value, tabs]);

  useEffect(() => {
    // Update indicator position on window resize
    const handleResize = () => updateIndicatorPosition();
    window.addEventListener('resize', handleResize);

    // Initial position calculation
    const timer = setTimeout(updateIndicatorPosition, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <StyledTabs
        ref={tabsRef}
        value={value}
        onChange={onChange}
        variant={variant}
        sx={getSizeStyles()}
        customBackgroundColor={backgroundColor}
        {...props}
      >
        <SlidingIndicator
          customActiveBackgroundColor={activeBackgroundColor}
          sx={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
          }}
        />
        {tabs.map((tab) => {
          const { label, labelKey, value, icon, iconPosition, ...tabProps } = tab;
          const displayLabel = labelKey ? t(labelKey, { ns: namespace }) : label;
          return (
            <StyledTab
              key={value}
              label={displayLabel}
              value={value}
              icon={icon}
              iconPosition={iconPosition || 'start'}
              customActiveTextColor={activeTextColor}
              customInactiveTextColor={inactiveTextColor}
              {...tabProps}
            />
          );
        })}
      </StyledTabs>
    </Box>
  );
};

export default ChipTabs; 